import { FloorplanSummaryItem } from 'src/store/hooks/useListFloorplans';

/**
 * Helpers shared by `FloorplanSelect` and `ZoneViewer`.
 *
 * That selected-date comparison is done on the calendar-date portion as
 * strings. goLiveDate is an ISO timestamp at Pacific midnight (e.g.
 * `2028-01-01T08:00:00Z`); the date picker emits UTC-midnight `YYYY-MM-DD`.
 * Comparing the `YYYY-MM-DD` prefixes as strings sidesteps the timezone
 * mismatch and sorts correctly.
 */

/** Returns the calendar-date portion (YYYY-MM-DD) of a floorplan's goLiveDate. */
export const goLiveDateString = (fp: FloorplanSummaryItem): string => fp.goLiveDate.split('T')[0];

/**
 * Returns the floorplan effective on `referenceDate` — the entry with the
 * latest goLiveDate on or before that date. Returns undefined if no entry is
 * effective on that date (e.g. the date is earlier than every floorplan).
 *
 * Used only to keep the dropdown selection in sync with a user-typed date;
 * the authoritative "currently live" floorplan comes from the backend's
 * `isLive` flag, not from this function.
 *
 * `referenceDate` is expected as `YYYY-MM-DD`.
 */
export const findEffectiveFloorplan = (
  floorplans: FloorplanSummaryItem[],
  referenceDate: string,
): FloorplanSummaryItem | undefined => {
  if (!referenceDate) return undefined;

  let best: FloorplanSummaryItem | undefined;
  let bestDate = '';
  for (const fp of floorplans) {
    const goLive = goLiveDateString(fp);
    if (goLive > referenceDate) continue;
    if (goLive > bestDate) {
      best = fp;
      bestDate = goLive;
    }
  }
  return best;
};
